import i18n from "@/i18n";
import UtilService from "@/services/UtilService";
import Restaurant from "@/domain/restaurant/Restaurant";
import TimeService from "@/services/TimeService";

export interface BookingIcon {
  icon: string;
  colour: string;
}

export interface BookingStatus {
  text: string;
  value: string;
  colour: string;
  active: boolean;
  icon: BookingIcon;
  allowSelection: boolean;
}

export interface TableAssignmentStatus {
  text: string;
  value: string;
  colour: string;
  icon: BookingIcon | null;
  tableChoice: boolean;
}

export interface BookingNotificationStatus {
  text: string;
  value: string;
  colour: string;
  icon: BookingIcon;
}

export default class Booking {
  bookingId!: number;
  numberOfPeople!: number;
  customerName!: string;
  phoneNumber?: string;
  email?: string;
  notes!: string;
  bookingDateTime!: string;
  creationDate!: string;
  statusUpdateDateTime!: string;
  tableId?: number;
  tableName?: string;
  language: string = "en";
  status!: string;
  tableAssignmentStatus!: string | undefined;
  preferredRoomId?: number;
  restaurantId?: number;
  notificationStatus!: string | undefined;
  notificationStatusUpdateDateTime!: string | undefined;

  restaurantDto!: Restaurant

  static MAX_NUMBER_OF_PEOPLE = 30;

  constructor(jsonObj?: Booking) {
    if (jsonObj) {
      Booking.updateBooking(this, jsonObj);
    }
  }

  static updateBooking(toUpdate: Booking, changed: Booking) {
    toUpdate.bookingId = changed.bookingId;
    toUpdate.numberOfPeople = changed.numberOfPeople;
    toUpdate.customerName = changed.customerName;
    toUpdate.email = changed.email;
    toUpdate.phoneNumber = changed.phoneNumber;
    toUpdate.notes = changed.notes;
    toUpdate.bookingDateTime = changed.bookingDateTime;
    toUpdate.creationDate = changed.creationDate;
    toUpdate.statusUpdateDateTime = changed.statusUpdateDateTime;
    toUpdate.tableId = changed.tableId;
    toUpdate.tableName = changed.tableName;
    toUpdate.language = changed.language;
    toUpdate.status = changed.status;
    toUpdate.tableAssignmentStatus = changed.tableAssignmentStatus;
    toUpdate.preferredRoomId = changed.preferredRoomId;
    toUpdate.restaurantId = changed.restaurantId;
    toUpdate.notificationStatus = changed.notificationStatus;
    toUpdate.notificationStatusUpdateDateTime =
      changed.notificationStatusUpdateDateTime;
    toUpdate.restaurantDto = changed.restaurantDto;
  }

  isRequestedTable() {
    return this.tableAssignmentStatus === "REQUESTED_TABLE";
  }

  setRequestedTable() {
    this.tableAssignmentStatus = "REQUESTED_TABLE";
  }

  setManualOverride() {
    this.tableAssignmentStatus = "MANUAL_OVERRIDE";
  }

  isManualOverride() {
    return this.tableAssignmentStatus === "MANUAL_OVERRIDE";
  }

  setVIP() {
    this.tableAssignmentStatus = "VIP";
  }

  isVIP() {
    return this.tableAssignmentStatus === "VIP";
  }

  isOnWaitingList() {
    return this.status === "WAITING_LIST";
  }

  isSeated() {
    return this.status === "SEATED";
  }

  isComplete() {
    return this.status === "COMPLETE";
  }

  getBookingTime() {
    return TimeService.getTimeFromISOString(this.bookingDateTime);
  }

  getTruncatedCustomerName() {
    if (this.customerName.length > 15)
      return this.customerName.slice(0, 15) + "...";
    else return this.customerName;
  }

  getShortTruncatedCustomerName() {
    if (this.customerName.length > 6)
      return this.customerName.slice(0, 6) + "...";
    else return this.customerName;
  }

  getTruncatedNotes() {
    if (this.notes != null && this.notes !== "") {
      if (this.notes.length > 15) return this.notes.slice(0, 15) + "...";
      else {
        return this.notes;
      }
    }
    return "";
  }

  static copy(toCopy: Booking): Booking {
    return new Booking(JSON.parse(JSON.stringify(toCopy)));
  }

  static haveDifferences(booking1: Booking, booking2: Booking) {
    return !UtilService.equals(booking1, booking2);
  }

  static getBookingStatusDisplayString(status: string): string {
    const bookingStatus = Booking.getBookingStatusFromString(status);
    if (bookingStatus != null) return bookingStatus.text;
    console.log("Status Display String Not Found");
    return status;
  }

  static getBookingStatusDisplayColour(status: string): string {
    const bookingStatus = Booking.getBookingStatusFromString(status);
    if (bookingStatus != null) return bookingStatus.colour;
    console.log("Status Display String Not Found");
    return status;
  }

  static getBookingStatusFromString(status: string): BookingStatus | null {
    for (let i = 0; i < Booking.bookingStatuses.length; i++)
      if (status === Booking.bookingStatuses[i].value)
        return Booking.bookingStatuses[i];
    return null;
  }

  static getTableAssignmentStatusFromString(
    status: string | null | undefined
  ): TableAssignmentStatus | null {
    for (let i = 0; i < Booking.tableAssignmentStatuses.length; i++)
      if (status === Booking.tableAssignmentStatuses[i].value)
        return Booking.tableAssignmentStatuses[i];
    return null;
  }

  static getStatusActive(status: string): boolean {
    const bookingStatus = Booking.getBookingStatusFromString(status);
    if (bookingStatus != null) return bookingStatus.active;
    console.log("Status Display String Not Found");
    return false;
  }

  static sortBookingStatusStringActive(status1: string, status2: string) {
    const active1 = Booking.getStatusActive(status1);
    const active2 = Booking.getStatusActive(status2);
    return active1 === active2 ? 0 : active1 ? -1 : 1;
    /* if both the same return 0, if 1 is true return -1 else 1 */
  }

  static sortBookingByTime(booking1: Booking, booking2: Booking) {
    return UtilService.stringComparatorAscending(
      booking1.bookingDateTime,
      booking2.bookingDateTime
    );
  }

  static sortBookingByStatusUpdateDateTime(
    booking1: Booking,
    booking2: Booking
  ) {
    return UtilService.stringComparatorAscending(
      booking1.statusUpdateDateTime,
      booking2.statusUpdateDateTime
    );
  }

  public getBookingStatusIcon(): BookingIcon {
    const bookingStatus = Booking.getBookingStatusFromString(this.status);
    if (bookingStatus != null) {
      return bookingStatus.icon;
    }
    console.log("STATUS NOT FOUND");
    return { icon: "", colour: "" };
  }

  public static isWaitingList(status: string) {
    return status === "WAITING_LIST";
  }

  static bookingStatuses: BookingStatus[] = [
    {
      text: i18n.tc("booking.status.seated"),
      value: "SEATED",
      colour: "green",
      icon: { icon: "mdi-seat-passenger", colour: "green" },
      active: true,
      allowSelection: true
    },
    {
      text: i18n.tc("booking.status.late"),
      value: "LATE",
      colour: "orange",
      icon: { icon: "mdi-clock-alert-outline", colour: "orange" },
      active: true,
      allowSelection: true
    },
    {
      text: i18n.tc("booking.status.confirmed"),
      value: "CONFIRMED",
      colour: "blue",
      icon: { icon: "mdi-check", colour: "blue" },
      active: true,
      allowSelection: true
    },
    {
      text: i18n.tc("booking.status.booked"),
      value: "BOOKED",
      colour: "grey darken-2",
      icon: { icon: "", colour: "" },
      active: true,
      allowSelection: true
    },
    {
      text: i18n.tc("booking.status.noShow"),
      value: "NO_SHOW",
      colour: "red",
      icon: { icon: "mdi-account-off", colour: "red" },
      active: false,
      allowSelection: true
    },
    {
      text: i18n.tc("booking.status.cancelled"),
      value: "CANCELLED",
      colour: "red",
      icon: { icon: "mdi-close-circle", colour: "red" },
      active: false,
      allowSelection: true
    },
    {
      text: i18n.tc("booking.status.cancelledOnline"),
      value: "CANCELLED_ONLINE",
      colour: "red",
      icon: { icon: "mdi-web-remove", colour: "red" },
      active: false,
      allowSelection: true
    },
    {
      text: i18n.tc("booking.status.archived"),
      value: "ARCHIVED",
      colour: "grey",
      icon: { icon: "mdi-archive-alert", colour: "gray" },
      active: false,
      allowSelection: false
    },
    {
      text: i18n.tc("booking.status.waitingList"),
      value: "WAITING_LIST",
      colour: "grey",
      icon: { icon: "mdi-human-queue", colour: "gray" },
      active: false,
      allowSelection: false
    },
    {
      text: i18n.tc("booking.status.complete"),
      value: "COMPLETE",
      colour: "blue",
      icon: { icon: "mdi-exit-run", colour: "blue" },
      active: true,
      allowSelection: false
    }
  ];

  static tableAssignmentStatuses: TableAssignmentStatus[] = [
    {
      text: i18n.tc("booking.assignment.status.requested"),
      value: "REQUESTED_TABLE",
      colour: "orange",
      icon: { icon: "mdi-lock", colour: "blue" },
      tableChoice: true
    },
    {
      text: i18n.tc("booking.assignment.status.manualOverride"),
      value: "MANUAL_OVERRIDE",
      colour: "red",
      icon: { icon: "mdi-alpha-m-box", colour: "red" },
      tableChoice: false
    },
    {
      text: i18n.tc("booking.assignment.status.autoAssignment"),
      value: "AUTO",
      colour: "green",
      icon: null,
      tableChoice: false
    },
    {
      text: i18n.tc("booking.assignment.status.vip"),
      value: "VIP",
      colour: "blue",
      icon: { icon: "mdi-star", colour: "yellow darken-3" },
      tableChoice: true
    },
    {
      text: i18n.tc("booking.assignment.status.assigned"),
      value: "ASSIGNED",
      colour: "grey",
      icon: { icon: "mdi-check-circle", colour: "grey" },
      tableChoice: false
    }
  ];

  static bookingNotificationStatuses: BookingNotificationStatus[] = [
    {
      text: i18n.tc("booking.notification.status.none"),
      value: "NONE",
      colour: "grey",
      icon: { icon: "mdi-bell-off", colour: "grey" }
    },
    {
      text: i18n.tc("booking.notification.status.initialEmail"),
      value: "INITIAL_EMAIL",
      colour: "orange",
      icon: { icon: "", colour: "" }
    },
    {
      text: i18n.tc("booking.notification.status.initialSMS"),
      value: "INITIAL_SMS",
      colour: "orange",
      icon: { icon: "", colour: "" }
    },
    {
      text: i18n.tc("booking.notification.status.confirmationEmail"),
      value: "CONFIRMATION_EMAIL",
      colour: "green",
      icon: { icon: "mdi-email-check", colour: "green" }
    },
    {
      text: i18n.tc("booking.notification.status.confirmationSMS"),
      value: "CONFIRMATION_SMS",
      colour: "green",
      icon: { icon: "mdi-cellphone-check", colour: "green" }
    },
    {
      text: i18n.tc("booking.notification.status.confirmationSMSEmail"),
      value: "CONFIRMATION_SMS_EMAIL",
      colour: "green",
      icon: { icon: "mdi-check", colour: "green" }
    },
    {
      text: i18n.tc("booking.notification.status.cancellationSent"),
      value: "CANCELLATION_SENT",
      colour: "red",
      icon: { icon: "mdi-book-cancel", colour: "red" }
    },
    {
      text: i18n.tc("booking.notification.status.noShowSent"),
      value: "NO_SHOW_SENT",
      colour: "red",
      icon: { icon: "mdi-account-cancel", colour: "red" }
    }
  ];

  public getTableAssignmentIcon(
    restaurant: Restaurant,
    showMovingInfo: boolean
  ): BookingIcon {
    const bookingStatus = Booking.getTableAssignmentStatusFromString(
      this.tableAssignmentStatus
    );
    if (bookingStatus != null && bookingStatus.icon != null) {
      return bookingStatus.icon;
    } else if (
      showMovingInfo &&
      showMovingInfo &&
      Restaurant.BookingTooLateToMove(restaurant, this)
    )
      return { icon: "mdi-sync-off", colour: "orange" };
    return { icon: "", colour: "" };
  }

  static getBookingCopyToCreateNewBooking(booking: Booking): Booking {
    const newBooking = new Booking();
    newBooking.customerName = booking.customerName;
    newBooking.numberOfPeople = booking.numberOfPeople;
    newBooking.phoneNumber = booking.phoneNumber;
    newBooking.email = booking.email;
    newBooking.notes = booking.notes;
    newBooking.language = booking.language;
    return newBooking;
  }
}
